/**
 * Message / step-log / thought-event slice — Phase 8 commit 83.
 *
 * Owns the dedupe + ownership-guard ladder for `addMessage` plus the
 * append-only `addStepLog` and `addThoughtEvent` actions.
 */

/** @internal – import from `@/store/useDepartmentStore` instead. */
import type { StoreApi } from "zustand";
import type { CoAgentState } from "../types";
import { generateId, nextLogId, nowTs } from "../utils";
import { expectedMessageSourceForKey } from "../messageOwnership";

type SetState = StoreApi<CoAgentState>["setState"];
type GetState = StoreApi<CoAgentState>["getState"];

const MESSAGE_DEDUPE_WINDOW_MS = 20_000;
const MESSAGE_SOFT_DEDUPE_WINDOW_MS = 4_000;

function withinMs(nowMs: number, timestamp: Date | undefined, windowMs: number): boolean {
  if (!timestamp) return false;
  return nowMs - timestamp.getTime() <= windowMs;
}

export type MessageActions = Pick<
  CoAgentState,
  "addMessage" | "addStepLog" | "addThoughtEvent"
>;

export function createMessageActions(set: SetState, _get: GetState): MessageActions {
  return {
    addMessage: (msg) => {
      set((state) => {
        const now = new Date();
        const nowMs = now.getTime();
        const source =
          msg.source ??
          (msg.role === "user" ? "user" : msg.role === "system" ? "system" : "pipeline");
        const dedupeKey = msg.dedupeKey?.trim();

        const expectedSource = expectedMessageSourceForKey(state.coAgentType, dedupeKey);
        if (expectedSource && source !== expectedSource) {
          if (process.env.NODE_ENV !== "production") {
            console.info("[coagent/messages] ownership-skip", {
              coAgentType: state.coAgentType,
              dedupeKey,
              source,
              expectedSource,
            });
          }
          return state;
        }

        if (dedupeKey) {
          for (let i = state.messages.length - 1; i >= 0; i -= 1) {
            const prior = state.messages[i];
            if (prior.dedupeKey !== dedupeKey) continue;
            if (withinMs(nowMs, prior.timestamp, MESSAGE_DEDUPE_WINDOW_MS)) {
              if (process.env.NODE_ENV !== "production") {
                console.info("[coagent/messages] dedupe-skip", { dedupeKey, source });
              }
              return state;
            }
            break;
          }
        }

        if (msg.role !== "user" && msg.content) {
          for (let i = state.messages.length - 1; i >= 0; i -= 1) {
            const prior = state.messages[i];
            if (!withinMs(nowMs, prior.timestamp, MESSAGE_SOFT_DEDUPE_WINDOW_MS)) break;
            if (
              prior.role === msg.role &&
              (prior.source ?? "") === source &&
              (prior.content ?? "") === msg.content &&
              (prior.widget ?? "") === (msg.widget ?? "")
            ) {
              if (process.env.NODE_ENV !== "production") {
                console.info("[coagent/messages] soft-dedupe-skip", {
                  source,
                  contentPreview: msg.content.slice(0, 80),
                });
              }
              return state;
            }
          }
        }

        return {
          messages: [
            ...state.messages,
            { ...msg, source, dedupeKey, id: generateId(), timestamp: now },
          ],
        };
      });
    },

    addStepLog: (stepIndex, text) => {
      set((state) => ({
        stepLogs: {
          ...state.stepLogs,
          [stepIndex]: [
            ...(state.stepLogs[stepIndex] ?? []),
            { id: nextLogId(), text, ts: nowTs() },
          ],
        },
      }));
    },

    addThoughtEvent: (stepIndex, event) => {
      set((state) => ({
        thoughtEvents: {
          ...state.thoughtEvents,
          [stepIndex]: [
            ...(state.thoughtEvents[stepIndex] ?? []),
            { ...event, id: nextLogId(), ts: nowTs() },
          ],
        },
      }));
    },
  };
}
